import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
    Flame, Thermometer, Snowflake, FileDown, Sparkles, PhoneCall, Mail,
    Search, Calendar, AlertTriangle, ExternalLink, Copy, Filter,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
    hot: {
        label: 'CHAUD',
        icon: Flame,
        color: 'text-red-400',
        bg: 'bg-red-500/10',
        border: 'border-red-500/30',
        desc: 'Demo demandée — à appeler en priorité',
    },
    warm: {
        label: 'TIÈDE',
        icon: Thermometer,
        color: 'text-amber-400',
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/30',
        desc: 'A téléchargé le PDF + fait le diagnostic — fort potentiel de closing',
    },
    tepid: {
        label: 'CURIEUX',
        icon: Sparkles,
        color: 'text-blue-400',
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/30',
        desc: 'A fait le diagnostic uniquement',
    },
    cold: {
        label: 'FROID',
        icon: Snowflake,
        color: 'text-slate-400',
        bg: 'bg-slate-500/10',
        border: 'border-slate-500/30',
        desc: 'A téléchargé le PDF — relance possible',
    },
};

function fmtDate(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
        if (diff === 0) return "aujourd'hui";
        if (diff === 1) return 'hier';
        if (diff < 7) return `il y a ${diff} j`;
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (_e) { return iso.slice(0, 10); }
}

export default function SuperAdminProspectsTab({ token }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ prospects: [], summary: {} });
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all'); // all|hot|warm|tepid|cold
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!token) return;
        const fetch = async () => {
            setLoading(true);
            try {
                const r = await axios.get('/api/super-admin/prospects', { headers: { Authorization: `Bearer ${token}` } });
                setData(r.data || { prospects: [], summary: {} });
            } catch (e) {
                toast.error('Erreur de chargement des prospects');
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [token]);

    const filtered = useMemo(() => {
        const list = data.prospects || [];
        return list.filter((p) => {
            if (filter !== 'all' && p.status !== filter) return false;
            if (!search) return true;
            const s = search.toLowerCase();
            return (
                (p.email || '').toLowerCase().includes(s) ||
                (p.agency_name || '').toLowerCase().includes(s) ||
                (p.first_name || '').toLowerCase().includes(s)
            );
        });
    }, [data.prospects, filter, search]);

    const copy = (text, label = 'Copié') => {
        navigator.clipboard.writeText(text);
        toast.success(label);
    };

    const summary = data.summary || {};

    if (loading) {
        return <div className="text-center py-12 text-white/50">Chargement du pipeline prospects…</div>;
    }

    return (
        <div className="space-y-6" data-testid="prospects-tab">
            {/* Header explanation */}
            <div className="rounded-lg bg-gradient-to-r from-[#681793]/10 to-[#FF914D]/10 border border-[#FF914D]/20 p-4">
                <p className="text-sm text-white/80">
                    <strong className="text-white">Pipeline Prospects</strong> — Croisement entre <strong>PDF téléchargé</strong>, <strong>Diagnostic interactif</strong> et <strong>Demande de démo</strong>. Concentrez-vous sur les <span className="text-amber-400 font-bold">TIÈDES</span> : ils ont déjà fait le diagnostic, vous savez précisément leurs angles morts.
                </p>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                    { key: 'all', label: 'Total', count: summary.total || 0, color: 'border-white/20', icon: Filter },
                    { key: 'hot', label: 'Chauds', count: summary.hot || 0, color: 'border-red-500/40', icon: Flame, txt: 'text-red-400' },
                    { key: 'warm', label: 'Tièdes', count: summary.warm || 0, color: 'border-amber-500/40', icon: Thermometer, txt: 'text-amber-400' },
                    { key: 'tepid', label: 'Curieux', count: summary.tepid || 0, color: 'border-blue-500/40', icon: Sparkles, txt: 'text-blue-400' },
                    { key: 'cold', label: 'Froids', count: summary.cold || 0, color: 'border-slate-500/40', icon: Snowflake, txt: 'text-slate-400' },
                ].map((c) => {
                    const Icon = c.icon;
                    const isActive = filter === c.key;
                    return (
                        <button
                            key={c.key}
                            type="button"
                            onClick={() => setFilter(c.key)}
                            className={`rounded-lg border ${c.color} p-3 text-left transition-all hover:bg-white/5 ${isActive ? 'bg-white/[0.06] ring-2 ring-white/20' : 'bg-white/[0.02]'}`}
                            data-testid={`prospect-filter-${c.key}`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <Icon className={`w-3.5 h-3.5 ${c.txt || 'text-white/60'}`} />
                                <span className="text-[10px] uppercase tracking-wider font-bold text-white/60">{c.label}</span>
                            </div>
                            <div className="text-2xl font-black text-white">{c.count}</div>
                        </button>
                    );
                })}
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                    type="text"
                    placeholder="Recherche par email, prénom ou agence…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF914D]/40 focus:ring-2 focus:ring-[#FF914D]/20"
                    data-testid="prospects-search"
                />
            </div>

            {/* Table */}
            <div className="rounded-lg border border-white/10 overflow-hidden bg-white/[0.02]">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-white/[0.04] text-white/60 text-[11px] uppercase tracking-wider">
                            <th className="text-left px-4 py-3 font-bold">Statut</th>
                            <th className="text-left px-4 py-3 font-bold">Contact</th>
                            <th className="text-left px-4 py-3 font-bold">Agence</th>
                            <th className="text-center px-2 py-3 font-bold">Activité</th>
                            <th className="text-center px-2 py-3 font-bold">Score</th>
                            <th className="text-left px-4 py-3 font-bold">Dernier signe</th>
                            <th className="text-right px-4 py-3 font-bold">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr><td colSpan={7} className="py-10 text-center text-white/40">Aucun prospect ne correspond aux filtres.</td></tr>
                        )}
                        {filtered.map((p) => {
                            const meta = STATUS_META[p.status] || STATUS_META.cold;
                            const Icon = meta.icon;
                            return (
                                <tr key={p.email} className="border-t border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`prospect-row-${p.email}`}>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full ${meta.bg} ${meta.border} border text-[10px] font-bold ${meta.color}`}>
                                            <Icon className="w-3 h-3" />
                                            {meta.label}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="text-white font-medium">{p.first_name || '—'}</div>
                                        <div className="text-white/40 text-xs flex items-center gap-1">
                                            <span className="truncate max-w-[160px]">{p.email}</span>
                                            <button onClick={() => copy(p.email, 'Email copié')} className="hover:text-white" title="Copier"><Copy className="w-3 h-3" /></button>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-white/70 text-xs">{p.agency_name || '—'}</td>
                                    <td className="px-2 py-3">
                                        <div className="flex items-center justify-center gap-1.5">
                                            <span title="PDF téléchargé" className={`text-[10px] ${p.has_pdf ? 'text-orange-400' : 'text-white/15'}`}>
                                                <FileDown className="w-3.5 h-3.5" />
                                            </span>
                                            <span title="Diagnostic complété" className={`text-[10px] ${p.has_diagnostic ? 'text-purple-400' : 'text-white/15'}`}>
                                                <Sparkles className="w-3.5 h-3.5" />
                                            </span>
                                            <span title="Demande de démo" className={`text-[10px] ${p.has_demo ? 'text-red-400' : 'text-white/15'}`}>
                                                <PhoneCall className="w-3.5 h-3.5" />
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-2 py-3 text-center">
                                        {p.diagnostic_score !== null ? (
                                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                                                p.diagnostic_score >= 70 ? 'bg-emerald-500/15 text-emerald-400'
                                                : p.diagnostic_score >= 45 ? 'bg-amber-500/15 text-amber-400'
                                                : 'bg-red-500/15 text-red-400'
                                            }`}>{p.diagnostic_score}/100</span>
                                        ) : <span className="text-white/20 text-xs">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-white/60 text-xs">{fmtDate(p.last_seen_at)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            type="button"
                                            onClick={() => setSelected(p)}
                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-gradient-to-r from-[#681793] to-[#FF914D] text-white text-xs font-bold hover:scale-[1.03] transition-transform"
                                            data-testid={`prospect-detail-${p.email}`}
                                        >
                                            Détails <ExternalLink className="w-3 h-3" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Detail drawer */}
            {selected && (
                <ProspectDetailDrawer prospect={selected} onClose={() => setSelected(null)} copy={copy} />
            )}
        </div>
    );
}

function ProspectDetailDrawer({ prospect: p, onClose, copy }) {
    const meta = STATUS_META[p.status] || STATUS_META.cold;
    const Icon = meta.icon;
    const mailto = `mailto:${p.email}?subject=Suite%20à%20votre%20diagnostic%20Homly`;
    return (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose} data-testid="prospect-detail-drawer">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div
                className="relative w-full max-w-md bg-[#0a0a1a] border-l border-white/10 overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-[#0a0a1a]/95 backdrop-blur-md border-b border-white/10 p-5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${meta.bg} ${meta.border} border text-[10px] font-bold ${meta.color}`}>
                            <Icon className="w-3.5 h-3.5" />
                            {meta.label}
                        </span>
                    </div>
                    <button onClick={onClose} className="text-white/40 hover:text-white text-2xl leading-none">×</button>
                </div>

                <div className="p-5 space-y-5">
                    <div>
                        <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Contact</p>
                        <p className="text-xl font-bold text-white">{p.first_name || 'Sans prénom'}</p>
                        <p className="text-white/70 text-sm flex items-center gap-2 mt-1">
                            <Mail className="w-3.5 h-3.5" />
                            {p.email}
                            <button onClick={() => copy(p.email, 'Email copié')} className="text-white/40 hover:text-white"><Copy className="w-3 h-3" /></button>
                        </p>
                        {p.phone && (
                            <p className="text-white/70 text-sm flex items-center gap-2 mt-1">
                                <PhoneCall className="w-3.5 h-3.5" />
                                <a href={`tel:${p.phone}`} className="text-white hover:underline">{p.phone}</a>
                                <button onClick={() => copy(p.phone, 'Téléphone copié')} className="text-white/40 hover:text-white"><Copy className="w-3 h-3" /></button>
                            </p>
                        )}
                        {p.agency_name && (
                            <p className="text-white/70 text-sm mt-1">🏢 {p.agency_name}</p>
                        )}
                    </div>

                    <div className={`rounded-lg ${meta.bg} ${meta.border} border p-3`}>
                        <p className={`text-xs ${meta.color} font-bold uppercase tracking-wider mb-1`}>Statut</p>
                        <p className="text-sm text-white">{meta.desc}</p>
                    </div>

                    {p.diagnostic_score !== null && (
                        <div className="rounded-lg bg-white/[0.03] border border-white/10 p-4">
                            <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Score Pilotage</p>
                            <div className="flex items-baseline gap-2 mb-3">
                                <span className={`text-4xl font-black ${
                                    p.diagnostic_score >= 70 ? 'text-emerald-400'
                                    : p.diagnostic_score >= 45 ? 'text-amber-400'
                                    : 'text-red-400'
                                }`}>{p.diagnostic_score}</span>
                                <span className="text-white/50">/ 100</span>
                                <span className="text-xs text-white/50 ml-2">· {p.diagnostic_level || '—'}</span>
                            </div>
                            {p.weak_kpis && p.weak_kpis.length > 0 && (
                                <>
                                    <p className="text-[10px] text-orange-400 uppercase tracking-wider font-bold mb-2">⚠️ 3 angles morts identifiés</p>
                                    <ul className="space-y-1.5">
                                        {p.weak_kpis.map((k, i) => (
                                            <li key={i} className="text-sm text-white/80 flex items-start gap-2">
                                                <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                                                <span>{k}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                    )}

                    <div>
                        <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Activité</p>
                        <div className="grid grid-cols-3 gap-2 text-center">
                            <div className={`rounded-lg p-2.5 ${p.has_pdf ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-white/[0.02] border border-white/5'}`}>
                                <FileDown className={`w-4 h-4 mx-auto mb-1 ${p.has_pdf ? 'text-orange-400' : 'text-white/20'}`} />
                                <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">PDF</p>
                                <p className="text-xs text-white mt-0.5">{p.pdf_count || 0} envoi(s)</p>
                            </div>
                            <div className={`rounded-lg p-2.5 ${p.has_diagnostic ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-white/[0.02] border border-white/5'}`}>
                                <Sparkles className={`w-4 h-4 mx-auto mb-1 ${p.has_diagnostic ? 'text-purple-400' : 'text-white/20'}`} />
                                <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">Diag.</p>
                                <p className="text-xs text-white mt-0.5">{p.diagnostic_count || 0}</p>
                            </div>
                            <div className={`rounded-lg p-2.5 ${p.has_demo ? 'bg-red-500/10 border border-red-500/20' : 'bg-white/[0.02] border border-white/5'}`}>
                                <PhoneCall className={`w-4 h-4 mx-auto mb-1 ${p.has_demo ? 'text-red-400' : 'text-white/20'}`} />
                                <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">Demo</p>
                                <p className="text-xs text-white mt-0.5">{p.has_demo ? (p.demo_status || 'new') : '—'}</p>
                            </div>
                        </div>
                    </div>

                    {p.events && p.events.length > 0 && (
                        <div>
                            <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Timeline ({p.events.length})</p>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {p.events.map((ev, i) => (
                                    <div key={i} className="flex items-start gap-2 text-xs">
                                        <Calendar className="w-3 h-3 text-white/30 mt-0.5 shrink-0" />
                                        <div>
                                            <span className="text-white/80 font-medium">
                                                {ev.type === 'pdf_download' && 'Téléchargement PDF'}
                                                {ev.type === 'diagnostic' && `Diagnostic · ${ev.score}/100`}
                                                {ev.type === 'demo_request' && `Demande démo · ${ev.status}`}
                                            </span>
                                            <span className="text-white/40 ml-2">{fmtDate(ev.at)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="pt-3 border-t border-white/10 flex gap-2">
                        <a
                            href={mailto}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#681793] to-[#FF914D] text-white text-sm font-bold hover:scale-[1.01] transition-transform"
                            data-testid="prospect-mailto"
                        >
                            <Mail className="w-4 h-4" />
                            Envoyer un email
                        </a>
                        {p.phone && (
                            <a
                                href={`tel:${p.phone}`}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 border border-white/15 text-white text-sm font-bold hover:bg-white/15"
                            >
                                <PhoneCall className="w-4 h-4" />
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
